import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import IAOciNriArticle from "../components/india/IAOciNriArticle";
import IAPromoStrip from "../components/india/IAPromoStrip";
import IALangBar from "../components/india/IALangBar";
import TripAutoHero from "../components/TripAutoHero";

const TITLE = "OCI & NRI Rules for Travel to & Living in India — IndianAirports.com";
const DESC = "NRI and OCI card rules explained: travel to India, living in India, benefits, restrictions, e-Arrival Card requirements, property and tax basics, plus official Government of India links. Updated September 2026.";

/* Standalone SEO page for the OCI & NRI guide — its own crawlable URL with
   title, meta description, canonical and Article JSON-LD. */
export default function IndiaOciGuide() {
  useEffect(() => {
    document.title = TITLE;

    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    const prevDesc = meta.content;
    meta.content = DESC;

    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = `${window.location.origin}/india/guides/oci-nri-rules`;
    document.head.appendChild(canonical);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Article",
      headline: "NRI and OCI Card Rules: Travel to India, Living in India, Benefits, Restrictions and Arrival Requirements",
      dateModified: "2026-09-01", inLanguage: "en",
      author: { "@type": "Organization", name: "IndianAirports.com" },
      publisher: { "@type": "Organization", name: "IndianAirports.com" },
      mainEntityOfPage: `${window.location.origin}/india/guides/oci-nri-rules`,
    });
    document.head.appendChild(ld);

    window.scrollTo(0, 0);
    return () => { meta.content = prevDesc; canonical.remove(); ld.remove(); };
  }, []);

  return (
    <div data-testid="india-oci-guide-page">
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="India temple gateway travel" alt="India travel" className="absolute inset-0 w-full h-full" testid="india-oci-guide-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-900/85 via-emerald-800/70 to-sky-700/60" />
        <div className="relative max-w-4xl mx-auto px-5 py-12">
          <Link to="/india/about" className="text-xs font-700 text-white/90 inline-flex items-center gap-1 hover:underline" data-testid="india-oci-guide-back">
            <ArrowLeft className="w-3.5 h-3.5" /> About IndianAirports.com
          </Link>
          <p className="mt-2 text-xs font-800 uppercase tracking-wide text-emerald-200 inline-flex items-center gap-1.5"><ScrollText className="w-3.5 h-3.5" /> Traveler guide</p>
          <h1 className="mt-1 font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white" data-testid="india-oci-guide-title">
            OCI & NRI Rules for Travel to & Living in India
          </h1>
          <p className="mt-2 text-base text-white/90 max-w-2xl">
            Everything travelers of Indian origin need to know — with section-by-section audio and official Government of India links.
          </p>
          <div className="mt-4"><IALangBar /></div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-5 py-8 space-y-10">
        <IAOciNriArticle />
        <IAPromoStrip city="India" />
      </div>
    </div>
  );
}
